/**
 * Network: batching, offline queue, retry (design §5).
 * - Content-type text/plain everywhere → CORS simple request, no preflight.
 * - HTTP 200 = transport OK → dequeue batch; never re-send individually-rejected events.
 * - Retry only on 5xx / 429 / network failure (respect Retry-After on 429).
 * - sendBeacon on page termination; fetch(keepalive) otherwise.
 */
import type { CollectEnvelope, CollectResponse, TrackEvent } from "./types";
import type { PixelStore } from "./storage";
import { byteLength, nowIso, safeJsonParse, safeJsonStringify } from "./util";
import { log, warn } from "./debug";

const FLUSH_N = 10;
const FLUSH_MS = 2000;
const MAX_BATCH = 50;
const MAX_BODY = 64 * 1024;
const BODY_HEADROOM = MAX_BODY - 2 * 1024;
const QUEUE_CAP = 100;
const RETRY_BASE_MS = 2000;
const RETRY_MAX_MS = 5 * 60 * 1000;
const RETRY_ATTEMPT_CAP = 12;
const QUEUE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const HIGH_VALUE: Record<string, true> = {
  lead_created: true,
  purchase: true,
  session_end: true,
};

export interface EnvelopeIds {
  visitorId?: string;
  sessionId?: string;
}

interface QueueEntry {
  item: TrackEvent;
  queuedAt: number;
  /** Number of whole-request transport failures observed for this event. */
  attempts: number;
}

function restoredEntry(value: unknown, now: number): QueueEntry | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Record<string, unknown>;

  // Current persisted format. Retry metadata lives beside the event and is never
  // included in the collector envelope.
  if (candidate.item && typeof candidate.item === "object") {
    const item = candidate.item as TrackEvent;
    if (typeof item.event !== "string" || typeof item.eventId !== "string")
      return null;
    const queuedAt =
      typeof candidate.queuedAt === "number" &&
      Number.isFinite(candidate.queuedAt)
        ? candidate.queuedAt
        : now;
    const attempts =
      typeof candidate.attempts === "number" &&
      Number.isFinite(candidate.attempts)
        ? Math.max(0, Math.floor(candidate.attempts))
        : 0;
    return { item, queuedAt, attempts };
  }

  // Backward compatibility for queues written before retry metadata was added.
  if (
    typeof candidate.event === "string" &&
    typeof candidate.eventId === "string"
  ) {
    return {
      item: candidate as unknown as TrackEvent,
      queuedAt: now,
      attempts: 0,
    };
  }
  return null;
}

export class Sender {
  private outbox: QueueEntry[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private attempt = 0;
  private nextOkAt = 0;
  private sending = false;

  constructor(
    private url: string,
    private key: string,
    private store: PixelStore,
    private getIds: () => EnvelopeIds,
    private onServerIds: (ids: EnvelopeIds) => void,
    private canSend: () => boolean = () => true,
  ) {
    const now = Date.now();
    const saved = safeJsonParse<unknown[]>(store.readQueue());
    if (Array.isArray(saved)) {
      this.outbox = saved
        .map((value) => restoredEntry(value, now))
        .filter((value): value is QueueEntry => value !== null)
        .slice(-QUEUE_CAP);
      this.pruneExpired(now);
      this.persist();
    }
    this.wire();
  }

  enqueue(ev: TrackEvent): void {
    if (!this.canSend()) return;
    this.pruneExpired(Date.now());
    this.outbox.push({ item: ev, queuedAt: Date.now(), attempts: 0 });
    if (this.outbox.length > QUEUE_CAP) {
      this.outbox.splice(0, this.outbox.length - QUEUE_CAP);
      warn("offline queue cap reached; dropped oldest");
    }
    this.persist();
    if (HIGH_VALUE[ev.event]) this.flush(false);
    else this.schedule();
  }

  private schedule(): void {
    if (this.timer) return;
    const now = Date.now();
    if (
      this.outbox.length >= FLUSH_N &&
      !this.sending &&
      now >= this.nextOkAt
    ) {
      this.flush(false);
      return;
    }
    const delay = Math.max(FLUSH_MS, this.nextOkAt - now);
    this.timer = setTimeout(() => {
      this.timer = null;
      this.flush(false);
    }, delay);
  }

  /** Flush buffered events. useBeacon=true on page termination. */
  flush(useBeacon: boolean): void {
    if (!this.canSend()) return;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.pruneExpired(Date.now())) this.persist();
    this.dropOversized();
    if (!this.outbox.length) return;
    if (!useBeacon && (this.sending || Date.now() < this.nextOkAt)) {
      this.schedule();
      return;
    }

    const batch: QueueEntry[] = [];
    let bytes = 0;
    for (const entry of this.outbox) {
      if (batch.length >= MAX_BATCH) break;
      const sz = byteLength(safeJsonStringify(entry.item));
      if (bytes + sz > BODY_HEADROOM && batch.length) break;
      batch.push(entry);
      bytes += sz;
    }
    const body = safeJsonStringify(
      this.envelope(batch.map((entry) => entry.item)),
    );

    if (useBeacon) {
      this.beacon(body, batch);
      return;
    }
    this.post(body, batch);
  }

  /** Drop queued events when a regulated-site hard gate closes. */
  clear(): void {
    this.outbox = [];
    this.attempt = 0;
    this.nextOkAt = 0;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.persist();
  }

  private envelope(events: TrackEvent[]): CollectEnvelope {
    const ids = this.getIds();
    return {
      key: this.key,
      visitorId: ids.visitorId,
      sessionId: ids.sessionId,
      sentAt: nowIso(),
      events,
    };
  }

  private beacon(body: string, batch: QueueEntry[]): void {
    try {
      const nav = navigator as Navigator & {
        sendBeacon?: (u: string, d?: BodyInit) => boolean;
      };
      if (nav.sendBeacon) {
        const blob = new Blob([body], { type: "text/plain" });
        const ok = nav.sendBeacon(this.url, blob);
        if (ok) {
          this.remove(batch);
          log("beacon flush", batch.length);
          return;
        }
      }
    } catch {
      /* fall through — keep queued */
    }
    this.persist();
  }

  private post(body: string, batch: QueueEntry[]): void {
    this.sending = true;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      this.sending = false;
    };
    try {
      fetch(this.url, {
        method: "POST",
        keepalive: true,
        headers: { "content-type": "text/plain" },
        body,
        // public collector: no credentials, no custom headers → simple request
        credentials: "omit",
      })
        .then((res) => {
          if (res.status === 200 || (res.status >= 200 && res.status < 300)) {
            // Transport succeeded. Dequeue whole batch; never re-send rejected events.
            this.attempt = 0;
            this.nextOkAt = 0;
            this.remove(batch);
            return res
              .json()
              .then((j: CollectResponse) => this.handleResults(j))
              .catch(() => undefined);
          }
          if (res.status === 429 || res.status >= 500) {
            this.backoff(this.retryAfter(res), batch);
          } else {
            // 4xx (bad key/origin/envelope) — drop batch, stop hammering.
            warn("collect rejected " + res.status + " — dropping batch");
            this.remove(batch);
          }
          return undefined;
        })
        .catch(() => {
          this.backoff(0, batch); // network failure → bounded retry
        })
        .finally(() => {
          finish();
          if (this.outbox.length) this.schedule();
        });
    } catch {
      finish();
      this.backoff(0, batch);
      this.schedule();
    }
  }

  private handleResults(j: CollectResponse): void {
    if (!j) return;
    if (j.visitorId || j.sessionId) {
      this.onServerIds({ visitorId: j.visitorId, sessionId: j.sessionId });
    }
    if (this.outbox.length === 0) return;
  }

  private backoff(retryAfterMs: number, batch: QueueEntry[]): void {
    const failedIds: Record<string, true> = {};
    for (const entry of batch) failedIds[entry.item.eventId] = true;
    for (const entry of this.outbox) {
      if (failedIds[entry.item.eventId]) entry.attempts += 1;
    }
    const pruned = this.pruneExpired(Date.now());
    if (pruned) warn("retry limit reached; dropped expired queued event");

    if (!this.outbox.length) {
      this.attempt = 0;
      this.nextOkAt = 0;
      this.persist();
      return;
    }
    this.attempt = Math.min(this.attempt + 1, 16);
    const base =
      retryAfterMs > 0
        ? Math.min(RETRY_MAX_MS, retryAfterMs)
        : Math.min(RETRY_MAX_MS, RETRY_BASE_MS * Math.pow(2, this.attempt - 1));
    const jitter = base * 0.2 * Math.random();
    this.nextOkAt = Date.now() + base + jitter;
    this.persist();
    log("backoff", Math.round((this.nextOkAt - Date.now()) / 1000) + "s");
  }

  private retryAfter(res: Response): number {
    try {
      const h = res.headers.get("retry-after");
      if (!h) return 0;
      const n = parseInt(h, 10);
      return isNaN(n) ? 0 : Math.min(RETRY_MAX_MS, Math.max(0, n * 1000));
    } catch {
      return 0;
    }
  }

  private remove(batch: QueueEntry[]): void {
    if (!batch.length) return;
    const ids: Record<string, true> = {};
    for (const entry of batch) ids[entry.item.eventId] = true;
    this.outbox = this.outbox.filter((entry) => !ids[entry.item.eventId]);
    this.persist();
  }

  private dropOversized(): void {
    const kept: QueueEntry[] = [];
    for (const entry of this.outbox) {
      if (byteLength(safeJsonStringify(entry.item)) > MAX_BODY) {
        warn("event exceeds 64KB — dropped", entry.item.event);
        continue;
      }
      kept.push(entry);
    }
    if (kept.length !== this.outbox.length) {
      this.outbox = kept;
      this.persist();
    }
  }

  /** Returns true when an entry was discarded. */
  private pruneExpired(now: number): boolean {
    const before = this.outbox.length;
    this.outbox = this.outbox.filter(
      (entry) =>
        entry.attempts < RETRY_ATTEMPT_CAP &&
        entry.queuedAt <= now &&
        now - entry.queuedAt <= QUEUE_MAX_AGE_MS,
    );
    return this.outbox.length !== before;
  }

  private persist(): void {
    this.store.writeQueue(safeJsonStringify(this.outbox));
  }

  private wire(): void {
    try {
      addEventListener("online", () => this.flush(false));
      const term = () => this.flush(true);
      addEventListener("pagehide", term);
      addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") this.flush(true);
      });
    } catch {
      /* noop */
    }
    if (this.outbox.length) this.schedule();
  }
}
